import React, { Fragment, ReactNode, SetStateAction, useRef } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { ErrorBoundaryTW } from '../errorBoundary/ErrorBoundaryTW';

export interface SlideOverProps {
  open: boolean;
  setOpen: React.Dispatch<SetStateAction<boolean>>;
  // usually a String
  title?: ReactNode;
  // usually a String
  description?: ReactNode;
  children?: ReactNode;
}

export const SlideOver: React.FC<SlideOverProps> = ({ children, ...rest }) => {
  const ref = useRef(null);
  return (
    <ErrorBoundaryTW>
      <Transition.Root show={rest.open} as={Fragment}>
        <Dialog as="div" className="relative z-10" onClose={rest.setOpen} initialFocus={ref}>
          <div className="fixed inset-0" />
          <div className="fixed inset-0 overflow-hidden">
            <div className="absolute inset-0 overflow-hidden">
              <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10">
                <Transition.Child
                  as={Fragment}
                  enter="transform transition ease-in-out duration-500 sm:duration-700"
                  enterFrom="translate-x-full"
                  enterTo="translate-x-0"
                  leave="transform transition ease-in-out duration-500 sm:duration-700"
                  leaveFrom="translate-x-0"
                  leaveTo="translate-x-full"
                >
                  <Dialog.Panel className="pointer-events-auto flex h-screen w-screen max-w-md flex-col">
                    <DefaultSlideOverHeader {...rest} />
                    <div className="grow overflow-y-auto bg-white py-6 shadow-xl" ref={ref}>
                      <div className="relative px-4 sm:px-6">{children}</div>
                    </div>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    </ErrorBoundaryTW>
  );
};

const DefaultSlideOverHeader: React.FC<SlideOverProps> = ({ setOpen, title, description }) => {
  return (
    <div className="grow-0 bg-gray-50 px-4 py-6 sm:px-6">
      <div className="flex items-start justify-between space-x-3">
        <div className="space-y-1">
          <Dialog.Title className="text-base font-semibold leading-6 text-gray-900">{title}</Dialog.Title>
          <p className="text-sm text-gray-500">{description}</p>
        </div>
        <div className="flex h-7 items-center">
          <button type="button" className="text-gray-400 hover:text-gray-500" onClick={() => setOpen(false)}>
            <span className="sr-only">Close panel</span>
            <XMarkIcon className="h-6 w-6" aria-hidden="true" />
          </button>
        </div>
      </div>
    </div>
  );
};
